import { Component, ElementRef, ViewChild, AfterViewInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { environment } from 'src/environments/environment';
import { NotificationService } from '../../services/notification.service';
import { AuthenticationService } from '../../services/authentication.service';
import { NavigationService } from '../../services/navigation.service';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { TranslateService } from '@ngx-translate/core';

interface GoogleCredentialResponse {
  credential?: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: GoogleInitializeOptions) => void;
          renderButton: (parent: HTMLElement | null, options: GoogleButtonOptions) => void;
        };
      };
    };
  }
}

interface GoogleButtonOptions {
  theme: string;
  text: string;
  size: string;
  width: number;
}

interface GoogleInitializeOptions {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
}

@Component({
    selector: 'fp-google-login-button',
    templateUrl: './google-login-button.component.html',
    styleUrl: './google-login-button.component.scss',
    standalone: false
})
export class GoogleLoginButtonComponent implements AfterViewInit, OnChanges {
  @ViewChild('googleButton') googleButton: ElementRef<HTMLDivElement>;
  @Input() signup: boolean = false;
  @Input() lang: string = 'en';
  @Input() deeplinkId?: string;
  constructor(
    private notificationService: NotificationService,
    private authService: AuthenticationService,
    private navigation: NavigationService,
    private errorHandler: ErrorHandlerService,
    private translate: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lang']) {
      this.loadGoogleIdentityScript().then(() => this.renderGoogleButton());
    }
  }

  ngAfterViewInit(): void {
    this.loadGoogleIdentityScript()
      .then(() => this.renderGoogleButton())
      .catch(() => this.notificationService.openSnackBar('error', this.translate.instant('AUTH.GOOGLE_LOGIN.UNAVAILABLE')));
  }

  private loadGoogleIdentityScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://accounts.google.com/gsi/client?hl=${this.lang}`;
      script.onload = () => resolve();
      script.onerror = () => reject();
      document.body.appendChild(script);
    });
  }

  private renderGoogleButton(): void {
    if (this.googleButton.nativeElement && window.google) {
      const googleInitializeOptions: GoogleInitializeOptions = {
        client_id: environment.googleClientId,
        callback: response => this.handleGoogleLogin(response),
      };

      window.google.accounts.id.initialize(googleInitializeOptions);
      window.google.accounts.id.renderButton(this.googleButton.nativeElement, {
        theme: 'outline',
        size: 'large',
        text: this.signup ? 'signup_with' : 'signin_with',
        width: 320,
      } as GoogleButtonOptions);
    }
  }

  private resolveDeeplinkId(): string | null {
    if (this.deeplinkId) {
      return this.deeplinkId;
    }
    // Deep link signup uses `uuid` as query param (see `DlSignupComponent`).
    const params = new URLSearchParams(window.location.search);
    return params.get('uuid') || params.get('deeplink') || null;
  }

  private async handleGoogleLogin(response: GoogleCredentialResponse): Promise<void> {
    if (!response.credential) {
      this.notificationService.openSnackBar('error', this.translate.instant('AUTH.GOOGLE_LOGIN.FAILED'));
      return;
    }
    try {
      const deeplinkId = this.resolveDeeplinkId();
      const user = await this.authService.socialLogin('google_oauth2', response.credential, this.signup, deeplinkId);
      if (user) {
        this.notificationService.openSnackBar(
          'success',
          this.signup
            ? this.translate.instant('AUTH.GOOGLE_LOGIN.SIGNUP_SUCCESS')
            : this.translate.instant('AUTH.GOOGLE_LOGIN.LOGIN_SUCCESS')
        );
        await this.navigation.processLogin(user.role);
      }
    } catch (error) {
      const message = error?.error.error || this.translate.instant('AUTH.GOOGLE_LOGIN.FAILED');
      this.notificationService.openSnackBar('error', this.errorHandler.translateError(message));
    }
  }
}
